import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import type { LoyaltyCard, LoyaltyCardStatusType } from '@/api/admin-loyalty-card-types'
import { useUpdateLoyaltyCard } from '@/api/api-mutations'
import { useListCustomers } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { useDebounce } from '@/utils/hooks'

export const Route = createFileRoute('/_authenticated/_general/loyalty-cards/$cardUuid/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.LoyaltyCards.EditTitle()
	}
})

function RouteComponent() {
	const { cardUuid } = Route.useParams()
	return (
		<RouteModal asDrawer requiresData loader={async api => await api.getLoyaltyCard({ path: { cardUuid } })}>
			{data => <EditLoyaltyCardForm card={data} />}
		</RouteModal>
	)
}

function EditLoyaltyCardForm({ card }: { card: LoyaltyCard }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { cardUuid } = Route.useParams()

	const updateCard = useUpdateLoyaltyCard(false, { customerUuid: card.customer?.uuid })

	const [customerSearch, setCustomerSearch] = useState('')
	const debouncedCustomerSearch = useDebounce(customerSearch)
	const customers = useListCustomers(
		{ limit: 20, offset: 0, q: debouncedCustomerSearch || undefined },
		{ enabled: debouncedCustomerSearch.length > 0 }
	)

	const customerOptions = useMemo(() => {
		const options: { value: string; label: string }[] = []
		if (card.customer) {
			const name = [card.customer.firstName, card.customer.lastName].filter(Boolean).join(' ')
			options.push({
				value: card.customer.uuid,
				label: name || card.customer.email || L.Customers.Anonymous()
			})
		}
		if (customers.data?.customers) {
			for (const c of customers.data.customers) {
				if (!options.some(o => o.value === c.uuid)) {
					const name = [c.firstName, c.lastName].filter(Boolean).join(' ')
					options.push({ value: c.uuid, label: name || c.email || L.Customers.Anonymous() })
				}
			}
		}
		return options
	}, [card.customer, customers.data, L])

	const form = useAppForm({
		defaultValues: {
			name: card.name,
			status: card.status as LoyaltyCardStatusType,
			availablePoints: card.availablePoints,
			totalPoints: card.totalPoints ?? card.availablePoints,
			customerUuid: card.customer?.uuid ?? ''
		},
		onSubmit: async ({ value }) => {
			const result = await updateCard({
				path: { cardUuid },
				body: {
					name: value.name,
					status: value.status,
					availablePoints: value.availablePoints,
					totalPoints: value.totalPoints,
					customerUuid: value.customerUuid || null
				}
			})
			if (result.isSuccess) {
				toast.success(L.LoyaltyCards.EditSuccess())
				handleSuccess()
			} else {
				toast.error(L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.LoyaltyCards.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField autoFocus label={L.Fields.Name()} />}
						/>
						<form.AppField
							name='status'
							children={field => (
								<field.SelectField
									label={L.Fields.Status()}
									options={[
										{ value: 'ACTIVE', label: L.Enum.CardStatus('ACTIVE') },
										{ value: 'INACTIVE', label: L.Enum.CardStatus('INACTIVE') },
										{ value: 'BLOCKED', label: L.Enum.CardStatus('BLOCKED') }
									]}
								/>
							)}
						/>
						<form.AppField
							name='availablePoints'
							validators={{
								onChange: ({ value }) =>
									typeof value !== 'number' || value < 0 ? L.General.Required() : undefined
							}}
							children={field => <field.NumberField label={L.LoyaltyCards.Column.AvailablePoints()} />}
						/>
						<form.AppField
							name='totalPoints'
							validators={{
								onChange: ({ value }) =>
									typeof value !== 'number' || value < 0 ? L.General.Required() : undefined
							}}
							children={field => <field.NumberField label={L.LoyaltyCards.Column.TotalPoints()} />}
						/>
						<form.AppField
							name='customerUuid'
							children={field => (
								<field.SingleComboboxField
									label={L.LoyaltyCards.Detail.Customer()}
									options={customerOptions}
									onSearchValueChange={setCustomerSearch}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
